// 후기의 순수 규칙 — 인메모리·Supabase·화면이 같은 한 벌을 쓴다(작성자 표기 · 공개 여부 · 쓸 수 있는 상태).

import { formatNumber } from "../../lib/format";
import { FINISHED_PROJECT_STATUSES, type ProjectStatus } from "../project/types";
import { MAX_AUTHOR_LABEL, MAX_REVIEW_BODY, type CustomerReview } from "./types";

export const REVIEW_EMPTY_MESSAGE = "후기를 적어 주세요";
export const REVIEW_TOO_LONG_MESSAGE = `${formatNumber(MAX_REVIEW_BODY)}자까지 적을 수 있습니다`;
export const REVIEW_NOT_OPEN_MESSAGE = "공사가 끝난 뒤에 후기를 남길 수 있습니다";
export const REVIEW_RATE_MESSAGE = "잠시 뒤 다시 저장해 주세요";
export const REVIEW_UNAVAILABLE_MESSAGE = "지금은 저장할 수 없습니다. 잠시 뒤 다시 저장해 주세요";
export const REVIEW_NOT_FOUND_MESSAGE = "후기를 찾을 수 없습니다";

export const ANONYMOUS_AUTHOR_LABEL = "익명 고객";
const MASK = "○○";
const SUFFIX = " 고객";

// 준공(유지보수·완료) 현장만 후기를 받는다 — `0044` customer_review_guard 와 같은 목록.
export const canReview = (status: ProjectStatus): boolean =>
  (FINISHED_PROJECT_STATUSES as readonly ProjectStatus[]).includes(status);

// 「김○○ 고객」 — 계약서 이름의 첫 글자(코드포인트)만 남긴다 · 이름이 없으면 「고객」 · 익명이면 「익명 고객」.
export const reviewAuthorLabelOf = (clientName: string | null | undefined, anonymous: boolean): string => {
  if (anonymous) return ANONYMOUS_AUTHOR_LABEL;
  const first = Array.from((clientName ?? "").trim())[0];
  const label = first ? `${first}${MASK}${SUFFIX}` : SUFFIX.trim();
  return Array.from(label).slice(0, MAX_AUTHOR_LABEL).join("");
};

export const isAnonymousLabel = (label: string): boolean => label === ANONYMOUS_AUTHOR_LABEL;

export type NormalizedReview = { ok: true; body: string } | { ok: false; message: string };

// 줄바꿈·탭 말고는 C0 제어문자(NUL 등)를 지운다 — PG text 는 NUL 을 못 받아 인메모리와 갈린다.
const CONTROL_CHARS = /[\u0000-\u0008\u000B-\u001F]/g;
// 앞뒤에서는 폭 없는 글자(\p{Cf} — U+200B·U+2060·U+FEFF 등)도 공백으로 본다.
const EDGE_BLANKS = /^[\s\p{Cf}]+|[\s\p{Cf}]+$/gu;

// 줄바꿈은 LF 로 · 제어문자는 지우고 · 앞뒤 공백은 뺀다 · 글자 수는 코드포인트(DB char_length 와 같은 자).
export const normalizeReviewBody = (raw: unknown): NormalizedReview => {
  const body = (typeof raw === "string" ? raw : "")
    .replace(/\r\n?/g, "\n")
    .replace(CONTROL_CHARS, "")
    .replace(EDGE_BLANKS, "");
  if (body === "") return { ok: false, message: REVIEW_EMPTY_MESSAGE };
  if (Array.from(body).length > MAX_REVIEW_BODY) return { ok: false, message: REVIEW_TOO_LONG_MESSAGE };
  return { ok: true, body };
};

// 동의를 유지하면 처음 동의한 시각을 지키고, 새로 켜면 지금, 끄면 null — `0044` customer_review_consent 와 짝.
export const consentedAtOf = (prev: string | null | undefined, consent: boolean, now: string): string | null =>
  consent ? (prev ?? now) : null;

// 공개 화면(나중 단계)에 나갈 수 있는 후기 — 고객 동의가 있고 직원이 숨기지 않았다.
export const isPublicReview = (review: Pick<CustomerReview, "consent_public" | "hidden">): boolean =>
  review.consent_public && !review.hidden;
